/**
 * Parts of the following code are from the ReactFlow Pro Layout example.
 * Copyright Webkid GmbH. All rights reserved.
 * Progranimate has been given the license to use this code for educational use under the following
 * conditions:
 *
 * https://www.xyflow.com/terms-of-use Section 2a (as of Friday, December 1)
 * Access to Pro Examples: Pro Examples are advanced examples built on top of the React Flow library.
 * The User may access the source code of all Pro Examples for the term of the Service Agreement.
 * Once the Service Agreement ends, the User will not have access to the source code of the Pro
 * Examples. Webkid may add, update, change or remove Pro Examples during the term of the Service
 * Agreement.The source code of the Pro Examples can be copied, adapted and reused by the User from
 * the start of the Service Agreement for an unlimited amount of time. The source code of the Pro
 * Examples is not allowed for redistribution, repackaging, or for building similar services as
 * React Flow Pro. The Pro Examples are provided on the React Flow Pro website. The Code is embedded
 * in the website and can be copied to the User's environment. Webkid is not liable for the provided
 * source code or any of it's dependencies. Webkid does not provide any guarantee that the code will
 * work in the User's environment.
 */
import { useEffect } from 'react';
import { Node, Edge, Position, ReactFlowState, useStore, useReactFlow } from 'reactflow';
import { stratify } from 'd3-hierarchy';
import { FlextreeNode, flextree } from 'd3-flextree';
import assert from 'assert';

// the layout direction (T = top, R = right, B = bottom, L = left, TB = top to bottom, ...)
export type Direction = 'TB' | 'LR' | 'RL' | 'BT';

export type Options = {
  direction: Direction;
};

const positionMap: Record<string, Position> = {
  T: Position.Top,
  L: Position.Left,
  R: Position.Right,
  B: Position.Bottom,
};

const getPosition = (x: number, y: number, direction: Direction) => {
  switch (direction) {
    case 'LR':
      return { x: y, y: x };
    case 'RL':
      return { x: -y, y: -x };
    case 'BT':
      return { x: -x, y: -y };
    default:
      return { x, y };
  }
};

// flextree is a d3 tree but it supports nodes of variable sizes.
const layout = flextree<Node>({
    nodeSize: (node) => [node.data.width * 2, node.data.height * 2],
    spacing: (a, b) => 0,
});
  
const nodeCountSelector = (state: ReactFlowState) => state.nodeInternals.size;
const nodesInitializedSelector = (state: ReactFlowState) =>
  Array.from(state.nodeInternals.values()).every((node) => node.width && node.height);

function useAutoLayout(options: Options, changeFlag: boolean) {
  const { direction } = options;
  const nodeCount = useStore(nodeCountSelector);
  const nodesInitialized = useStore(nodesInitializedSelector);
  const { getNodes, getEdges, setNodes, setEdges, fitView } = useReactFlow();
  console.log(changeFlag)
  useEffect(() => {
    console.log("layouting is occuring")
    // only run the layout if there are nodes and they have been initialized with their dimensions
    if (!nodeCount || !nodesInitialized) {
      return;
    }

    const nodes: Node[] = getNodes();
    const edges: Edge[] = getEdges();

    // not sure why this is neccesary - Collins
    if(nodes.length == 0 || nodes.some(node => node.width === undefined || node.height === undefined)) {
      return;
    }
    // stratify takes table-like data and turns it into 
    const hierarchy = stratify<Node>()
      .id((d) => d.id)
      // get the id of each node by searching through the edges
      // It's searching for nodes going from Bottom to Top / Right to Top to be considered a parent.
      .parentId((d: Node) => edges.find((e: Edge) => e.target === d.id && (e.sourceHandle == Position.Bottom || e.sourceHandle == Position.Right) && e.targetHandle == Position.Top)?.source)
      (nodes);

    // run the layout algorithm with the hierarchy data structure
    const root = layout(hierarchy);

    // this does 3 things in a janky way to convert from a Tree layout to a 
    var yOffset = 0;
    var xOffset = 0;
    root.each((node) => {
        // @ts-ignore
        node.x += xOffset;
        // @ts-ignore
        node.y += yOffset;
        const id = node.data.id;

        // STEP 1
        const parentEdge = edges.find(edge => edge.target == id && edge.sourceHandle == Position.Bottom && edge.targetHandle == Position.Top);
        if(parentEdge) {
            // just getting node.data.parent 
            const parentNode = nodes.find(node => node.id == parentEdge.source);
            assert(parentNode, 'parent node not found.')
            xOffset += (node.parent.x + (parentNode.width - node.data.width) / 2) - node.x;
            
            // @ts-ignore read only property may be dangerous but it works and I'm not sure if there's
            // another way to do this.
            node.x = node.parent.x + (parentNode.width - node.data.width) / 2;
        }
        else{
            console.log(xOffset)
            console.log(node)
        }

        const jumpBack = edges.find(edge => edge.target == id && edge.sourceHandle == Position.Bottom && edge.targetHandle == Position.Right);
        if(jumpBack) {
            const newNode = nodes.find(node => node.id == jumpBack.source);
            assert(newNode, 'parent node not found.')
            const no = root.find(node => node.data == newNode);
            // 15 is a magic number, seems to be the margin.
            const offset = no.y + no.data.height + 15;
            if(offset > node.y) {
                //@ts-ignore
                node.y = offset;
                yOffset += no.data.height + 15;
            }
        }
    })
    

    // set the React Flow nodes with the positions from the layout
    setNodes((nodes) =>
      nodes.map((node) => {
        // find the node in the hierarchy with the same id and get its coordinates
        let { x, y, data } = root.find((d) => d.id === node.id) || { x: node.position.x, y: node.position.y };

        // When parent is set in a ReactFlow node, the position becomes relevant to the parent, this
        // modifies the behavior so it's relevant to 0,0 again.
        if(data.parentNode) {
          const parent = root.find((d) => d.id === data.parentNode);
          x -= parent.x;
          y -= parent.y;
        }
        return {
          ...node,
          sourcePosition: positionMap[direction[1]],
          targetPosition: positionMap[direction[0]],
          position: getPosition(x, y, direction),
          style: { opacity: 1 },
        };
      })
    );

    setEdges((edges) => edges.map((edge) => ({ ...edge, style: { opacity: 1 } })));
  }, [nodeCount, nodesInitialized, getNodes, getEdges, setNodes, setEdges, fitView, direction, changeFlag]);
}

export default useAutoLayout;
